import React, { useState, useRef, useEffect } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Mail, Shield } from 'lucide-react';

interface VerifyLoginCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string) => void;
  isLoading?: boolean;
  email: string;
}

export const VerifyLoginCodeModal: React.FC<VerifyLoginCodeModalProps> = ({
  isOpen,
  onClose,
  onVerify,
  isLoading = false,
  email,
}) => {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus primer input al abrir modal
  useEffect(() => {
    if (isOpen) {
      inputRefs.current[0]?.focus();
    }
  }, [isOpen]);

  const handleChange = (index: number, value: string) => {
    // Solo permitir números
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Solo el último dígito
    setCode(newCode);

    // Auto-focus siguiente input si hay valor
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace: ir al input anterior
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Validar que sea código de 6 dígitos
    if (!/^\d{6}$/.test(pastedData)) {
      return;
    }

    const newCode = pastedData.split('');
    setCode(newCode);
    
    // Focus último input
    inputRefs.current[5]?.focus();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    if (fullCode.length === 6) {
      onVerify(fullCode);
    }
  };

  const isCodeComplete = code.every(digit => digit !== '');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <form onSubmit={handleSubmit}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="text-blue-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Verificación de Seguridad
          </h2>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <Mail size={18} />
            <p className="text-sm">
              Código enviado a: <strong>{email}</strong>
            </p>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 text-center mb-4">
            Ingresa el código de 6 dígitos
          </label>

          {/* Inputs de código */}
          <div 
            className="flex justify-center gap-2 mb-4"
            onPaste={handlePaste}
          >
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                disabled={isLoading}
                autoComplete="off"
              />
            ))}
          </div>

          <p className="text-xs text-gray-500 text-center">
            💡 Tip: Puedes pegar el código completo
          </p>
        </div>

        {/* Instrucciones */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800 mb-2">
            📧 <strong>Revisa tu correo electrónico</strong>
          </p>
          <ul className="text-xs text-blue-700 space-y-1 ml-4">
            <li>• El código es válido por 10 minutos</li>
            <li>• Tienes máximo 5 intentos</li>
            <li>• Si no lo recibes, verifica la carpeta de spam</li>
          </ul>
        </div>

        {/* Botones */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
            isLoading={isLoading}
            disabled={!isCodeComplete || isLoading}
          >
            Verificar Código
          </Button>
        </div>

        {/* Link para reenviar */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
            disabled={isLoading}
          >
            ¿No recibiste el código? Volver a intentar
          </button>
        </div>
      </form>
    </Modal>
  );
};
